import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { tokens } from '@/theme'

interface Props {
  open: boolean
  onClose: () => void
  /** Title rendered in the header. Doubles as the dialog's accessible name. */
  title: string
  /** Optional small uppercase label above the title. */
  eyebrow?: string
  /**
   * Max width of the panel. Forms benefit from ~520; long-form content can
   * grow up to ~720 before legibility falls off.
   */
  maxWidth?: number
  /** Footer content (typically buttons). Rendered on a separate row. */
  footer?: ReactNode
  children: ReactNode
}

/**
 * Lightweight modal overlay. Centered panel on a dimmed backdrop.
 *
 * Behaviours:
 *   - Escape closes
 *   - Backdrop click closes
 *   - First focusable element inside the body is focused on open
 *   - Body scroll is locked while the modal is open
 *
 * Not a portal — React 18 with the existing layout has no z-index conflicts.
 * The panel is positioned `fixed` so it floats above the rest of the app.
 */
export function Modal({ open, onClose, title, eyebrow, maxWidth = 520, footer, children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Escape key + body scroll lock
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // Focus first focusable on open
  useEffect(() => {
    if (!open || !panelRef.current) return
    const focusable = panelRef.current.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    )
    focusable?.focus()
  }, [open])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(17, 17, 17, 0.42)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '64px 24px',
        overflowY: 'auto',
        animation: `fadeInSoft ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <div
        ref={panelRef}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth,
          background: tokens.paper,
          border: `1px solid ${tokens.line2}`,
          borderRadius: tokens.radiusMd,
          fontFamily: tokens.fontDisplay,
          boxShadow: '0 20px 60px -20px rgba(17, 17, 17, 0.35)',
          animation: `slideUp ${tokens.durStd} ${tokens.easeStandard}`,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '24px 28px 20px',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {eyebrow && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div style={{ width: 24, height: 2, background: tokens.gold }} />
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: tokens.weightDemi,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: tokens.gold,
                  }}
                >
                  {eyebrow}
                </span>
              </div>
            )}
            <h2
              style={{
                fontSize: 22,
                fontWeight: tokens.weightDemi,
                color: tokens.ink,
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {title}
            </h2>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div style={{ padding: '20px 28px' }}>{children}</div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: '16px 28px 24px',
              borderTop: `1px solid ${tokens.line}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Close"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        background: 'transparent',
        border: 'none',
        borderRadius: tokens.radiusMd,
        cursor: 'pointer',
        color: tokens.ink3,
        flexShrink: 0,
        transition: `background ${tokens.durFast} ${tokens.easeStandard}, color ${tokens.durFast} ${tokens.easeStandard}`,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = tokens.paper3
        e.currentTarget.style.color = tokens.ink
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = tokens.ink3
      }}
    >
      <X size={16} strokeWidth={1.6} />
    </button>
  )
}
